import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import EssayRequirementsForm from './EssayRequirementsForm';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

const EssayRequirementsModal = ({ isOpen, onClose, collegeId, collegeName, onSaveCallback }) => {
  const [requirements, setRequirements] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && collegeId) {
      loadRequirements();
    }
  }, [isOpen, collegeId]);

  const loadRequirements = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .select('essay_requirements')
        .eq('id', collegeId)
        .single();

      if (error) throw error;
      setRequirements(data?.essay_requirements || {});
    } catch (error) {
      console.error('Error loading essay requirements:', error);
      toast.error('Failed to load essay requirements');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (updatedRequirements) => {
    try {
      const { error } = await supabase
        .from('college_journey_colleges')
        .update({ 
          essay_requirements: updatedRequirements,
          updated_at: new Date().toISOString()
        })
        .eq('id', collegeId);

      if (error) throw error;

      toast.success('Essay requirements saved successfully');
      
      // Trigger slot generation if this is the first time requirements are added
      if (!requirements || Object.keys(requirements).length === 0) {
        toast.info('Essay slots will be created automatically');
      }
      
      // Notify parent component of changes for immediate UI updates
      if (onSaveCallback) {
        await onSaveCallback(updatedRequirements);
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving essay requirements:', error);
      toast.error('Failed to save essay requirements');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-stone-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="relative inline-block w-full max-w-full sm:max-w-4xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              onClick={onClose}
              className="text-stone-400 hover:text-stone-500 focus:outline-none"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="mt-2">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azure-600"></div>
              </div>
            ) : (
              <EssayRequirementsForm
                collegeId={collegeId}
                collegeName={collegeName}
                initialRequirements={requirements}
                onSave={handleSave}
                onCancel={onClose}
                embedded={false}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EssayRequirementsModal;